



















































































import { VideoContainer } from '@/models/ResponsiveVideo';
import ResponsiveVideos from '@/components/Blocks/ResponsiveVideos.vue';
import YoutubePopupLink from '../Blocks/YoutubePlaylist/YoutubePopupLink.vue';
import Vue, { PropType } from 'vue';

type ResizeObserverInstance = {
  observe(target: Element): void;
  disconnect(): void;
};

type ResizeObserverConstructor = new (callback: () => void) => ResizeObserverInstance;

type CarouselItem = {
  id: string;
  h1: string;
  h2: string;
  h3: string;
  h4: string;
  text: string;
  textPosition?: "text-right" | "text-left" | "text-below";
  backgroundColor?: string;
  forgroundImage?: string;
  forgroundAlt?: string;
  backgroundImage?: string;
  linkUrl?: string;
  linkText?: string;
  linkTarget?: string;
  bright?: boolean;
  videos?: VideoContainer;
}

export default Vue.extend({
  name: 'CarouselComponent',
  components: {
    ResponsiveVideos,
    YoutubePopupLink
  },
  props: {
    initialItems: {
      type: Array as PropType<CarouselItem[]>,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: true,
      resizeObserver: null as ResizeObserverInstance | null,
      items: this.initialItems
    };
  },
  watch: {
    items: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.checkScroll();
          }, 250); 
        });
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.checkScroll();
      }, 250);

      const track = this.$refs.track as HTMLElement;
      const resizeObserverConstructor = (window as any).ResizeObserver as ResizeObserverConstructor | undefined;
      if (track && resizeObserverConstructor) {
        this.resizeObserver = new resizeObserverConstructor(() => {
          this.checkScroll();
        });
        this.resizeObserver.observe(track);
      }
    });
    
    window.addEventListener('resize', this.checkScroll);
  },
  destroyed() {
    window.removeEventListener('resize', this.checkScroll);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    checkScroll(): void {
      const track = this.$refs.track as HTMLElement;
      if (!track) return;

      this.canScrollLeft = track.scrollLeft > 0;
      this.canScrollRight = Math.ceil(track.scrollLeft + track.clientWidth) < (track.scrollWidth - 1);
    },
    
    smoothScroll(track: HTMLElement, targetScroll: number, duration: number): void {
      const startScroll = track.scrollLeft;
      const distance = targetScroll - startScroll;
      if (distance === 0) return;

      track.style.setProperty('scroll-snap-type', 'none');
      let startTime: number | null = null;
      
      if ((track as any)._scrollAnimationId) {
        cancelAnimationFrame((track as any)._scrollAnimationId);
      }

      const animation = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        
        const ease = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        track.scrollLeft = startScroll + (distance * ease);

        if (timeElapsed < duration) {
          (track as any)._scrollAnimationId = requestAnimationFrame(animation);
        } else {
          track.scrollLeft = targetScroll;
          track.style.setProperty('scroll-snap-type', 'x mandatory');
          (track as any)._scrollAnimationId = null;
          this.checkScroll(); 
        }
      };

      (track as any)._scrollAnimationId = requestAnimationFrame(animation);
    },

    getSnappedPositions(): number[] {
      const track = this.$refs.track as HTMLElement;
      const items = Array.from(track.querySelectorAll('.carousel-item')) as HTMLElement[];
      
      const trackRect = track.getBoundingClientRect();
      const maxScroll = track.scrollWidth - track.clientWidth;

      return items.map(item => {
        const itemRect = item.getBoundingClientRect();
        const absoluteLeft = (itemRect.left - trackRect.left) + track.scrollLeft;

        return Math.max(0, Math.min(Math.round(absoluteLeft), maxScroll));
      });
    },

    getCurrentIndex(positions: number[]): number {
      const track = this.$refs.track as HTMLElement;
      const currentScroll = Math.round(track.scrollLeft);
      
      let closestIndex = 0;
      let minDiff = Infinity;

      positions.forEach((pos, index) => {
        const diff = Math.abs(pos - currentScroll);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = index;
        }
      });
      
      return closestIndex;
    },

    nextSlide(): void {
      const track = this.$refs.track as HTMLElement;
      if (!track) return;

      const positions = this.getSnappedPositions();
      if (!positions.length) return;

      const currentIndex = this.getCurrentIndex(positions);
      const currentScroll = Math.round(track.scrollLeft);
      
      let targetIndex = currentIndex + 1;
      let targetScroll = currentScroll;

      while (targetIndex < positions.length) {
        targetScroll = positions[targetIndex];
        if (targetScroll - currentScroll > 2) break;
        targetIndex++;
      }

      if (targetScroll !== currentScroll) {
        this.smoothScroll(track, targetScroll, 400);
      }
    },
    
    prevSlide(): void {
      const track = this.$refs.track as HTMLElement;
      if (!track) return;

      const positions = this.getSnappedPositions();
      if (!positions.length) return;

      const currentIndex = this.getCurrentIndex(positions);
      const currentScroll = Math.round(track.scrollLeft);
      
      let targetIndex = currentIndex - 1;
      let targetScroll = currentScroll;
      while (targetIndex >= 0) {
        targetScroll = positions[targetIndex];
        if (currentScroll - targetScroll > 2) break;
        targetIndex--;
      }

      if (targetScroll !== currentScroll) {
        this.smoothScroll(track, targetScroll, 400);
      }
    }
  }
});
